'use client';

import type React from 'react';
import { cn } from '../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from './avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

export interface UserProfileSummary {
  id?: number | string | null;
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
  jobTitle?: string | null;
}

export function getUserInitials(user?: UserProfileSummary | null, fallback = '??'): string {
  if (!user) return fallback;

  if (user.firstName || user.lastName) {
    const f = user.firstName ? user.firstName.charAt(0) : '';
    const l = user.lastName ? user.lastName.charAt(0) : '';
    const combined = (f + l).toUpperCase();
    if (combined) return combined.slice(0, 2);
  }

  if (user.fullName && user.fullName.trim()) {
    const parts = user.fullName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase().slice(0, 2);
    }
    return parts[0].slice(0, 2).toUpperCase();
  }

  if (user.email && user.email.trim()) {
    return user.email.trim().slice(0, 2).toUpperCase();
  }

  return fallback;
}

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const SIZE_CLASSES: Record<AvatarSize, { avatar: string; fallbackText: string }> = {
  xs: { avatar: 'size-6', fallbackText: 'text-[10px]' },
  sm: { avatar: 'size-7', fallbackText: 'text-xs' },
  md: { avatar: 'size-8', fallbackText: 'text-xs' },
  lg: { avatar: 'size-10', fallbackText: 'text-sm' },
  xl: { avatar: 'size-12', fallbackText: 'text-base' },
};

export interface UserAvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  user?: UserProfileSummary | null;
  name?: string | null;
  avatarUrl?: string | null;
  jobTitle?: string | null;
  size?: AvatarSize;
  showTooltip?: boolean;
  tooltipSide?: 'top' | 'bottom' | 'left' | 'right';
  avatarClassName?: string;
  fallbackClassName?: string;
}

export function UserAvatar({
  user,
  name,
  avatarUrl,
  jobTitle,
  size = 'md',
  showTooltip = true,
  tooltipSide = 'top',
  className,
  avatarClassName,
  fallbackClassName,
  ...props
}: UserAvatarProps) {
  const effectiveUser: UserProfileSummary = {
    fullName: user?.fullName ?? name ?? null,
    avatarUrl: user?.avatarUrl ?? avatarUrl ?? null,
    jobTitle: user?.jobTitle ?? jobTitle ?? null,
    firstName: user?.firstName ?? null,
    lastName: user?.lastName ?? null,
    email: user?.email ?? null,
  };

  const displayName =
    effectiveUser.fullName ||
    [effectiveUser.firstName, effectiveUser.lastName].filter(Boolean).join(' ') ||
    effectiveUser.email ||
    'User';

  const initials = getUserInitials(effectiveUser);
  const sizeConfig = SIZE_CLASSES[size] || SIZE_CLASSES.md;

  const avatarNode = (
    <Avatar className={cn(sizeConfig.avatar, 'shrink-0', avatarClassName, className)} {...props}>
      <AvatarImage src={effectiveUser.avatarUrl || undefined} alt={displayName} />
      <AvatarFallback
        className={cn(
          'font-semibold bg-muted text-muted-foreground',
          sizeConfig.fallbackText,
          fallbackClassName
        )}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );

  if (!showTooltip || !displayName) {
    return avatarNode;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{avatarNode}</TooltipTrigger>
      <TooltipContent side={tooltipSide} className="text-xs px-2.5 py-1">
        <p className="font-medium">{displayName}</p>
        {effectiveUser.jobTitle && (
          <p className="text-[10px] opacity-80 font-normal">{effectiveUser.jobTitle}</p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
